import { Link, useLocation } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { getNicheThemeStyle } from "../../utils/nicheTheme";
import { getNicheMeta } from "../../components/niche/nicheMeta";
import { loadCreatorStudioData } from "../../services/creatorStudioService";
import { isFoundingCreator } from "../../services/dropPublishService";
import { isSuperAdmin } from "../../utils/roleUtils";
import { subscribeAdminBadges } from "../../services/adminBadgeService";
import { computeMonthlyEarnings, formatCount, formatMoney, formatMoneyWhole, nextPayoutDate } from "./creatorHelpers";

import "./creatorStudio.css";

const STROKE = 2;

function NavIcon({ children }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={STROKE} strokeLinecap="round" strokeLinejoin="round">
      {children}
    </svg>
  );
}

/** Superadmin-only shortcut links (same routes as AdminLayout). */
const ADMIN_TOOL_LINKS = [
  {
    to: "/admin/approvals/creators",
    label: "Creator Approvals",
    badgeKey: "pendingCreators",
    badgeClass: "cs-badge-gold",
    icon: <NavIcon><circle cx="12" cy="12" r="9" /><path d="M8 12.5l2.5 2.5L16 9.5" /></NavIcon>,
  },
  {
    to: "/admin/approvals/fans",
    label: "Fan Waitlist",
    badgeKey: "waitlistedFans",
    badgeClass: "cs-badge-gold",
    icon: (
      <NavIcon>
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 00-3-3.87" />
        <path d="M16 3.13a4 4 0 010 7.75" />
      </NavIcon>
    ),
  },
  {
    to: "/admin/support",
    label: "Support Inbox",
    badgeKey: "openTickets",
    highKey: "highPriorityTickets",
    badgeClass: "cs-badge-gold",
    icon: <NavIcon><path d="M4 6h16v12H4z" /><path d="m4 7 8 6 8-6" /></NavIcon>,
  },
  {
    to: "/admin/email-templates",
    label: "Email Templates",
    icon: <NavIcon><path d="M4 5h16v14H4z" /><path d="m4 7 8 6 8-6" /><path d="M8 15h5" /></NavIcon>,
  },
  {
    to: "/admin/outreach",
    label: "Outreach Drafts",
    icon: <NavIcon><path d="M22 2L11 13" /><path d="M22 2l-7 20-4-9-9-4 20-7z" /></NavIcon>,
  },
  {
    to: "/admin/users",
    label: "Users",
    icon: <NavIcon><circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" /></NavIcon>,
  },
  {
    to: "/admin/settings",
    label: "Settings",
    icon: (
      <NavIcon>
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
      </NavIcon>
    ),
  },
];

const NAV_SECTIONS = [
  {
    label: "My Profile",
    links: [
      {
        label: "Edit Profile",
        dynamicTo: "edit",
        icon: <NavIcon><circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" /></NavIcon>,
      },
      {
        label: "Theme Catalog",
        to: "/themes",
        icon: <NavIcon><circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" /></NavIcon>,
      },
    ],
  },
  {
    label: "Drops",
    links: [
      {
        to: "/my-drops",
        label: "Manage Drops",
        icon: <NavIcon><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></NavIcon>,
        badgeKey: "openCount",
        badgeClass: "cs-badge-green",
        badgeFormat: (n) => `${n} live`,
      },
      {
        to: "/post",
        label: "Post a Drop",
        icon: <NavIcon><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></NavIcon>,
      },
      {
        to: "/schedule-drop",
        label: "Scheduled Drops",
        icon: <NavIcon><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></NavIcon>,
        badgeKey: "scheduledCount",
        badgeClass: "cs-badge-gold",
        badgeFormat: (n) => `${n} upcoming`,
      },
    ],
  },
  {
    label: "Monetisation",
    links: [
      {
        to: "/support-tiers",
        label: "Subscription Tiers",
        icon: <NavIcon><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" /></NavIcon>,
      },
    ],
  },
  {
    label: "Community",
    links: [
      {
        to: "/appreciation-wall",
        label: "Appreciation Wall",
        icon: <NavIcon><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" /></NavIcon>,
      },
      {
        to: "/discover",
        label: "Featured Creator",
        icon: <NavIcon><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></NavIcon>,
        staticBadge: "Nominated",
        badgeClass: "cs-badge-gold",
      },
      {
        to: "/analytics",
        label: "Analytics",
        icon: <NavIcon><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></NavIcon>,
      },
      {
        to: "/broadcasts",
        label: "Broadcast",
        icon: (
          <NavIcon>
            <path d="M3 11l18-5v12L3 13v-2z" />
            <path d="M11.6 16.2a2.8 2.8 0 01-2.8 2.6" />
            <path d="M19 8.5v.01" />
          </NavIcon>
        ),
      },
    ],
  },
  {
    label: "Account",
    links: [
      {
        to: "/dashboard",
        label: "Dashboard",
        icon: <NavIcon><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></NavIcon>,
      },
      {
        to: "/subscribers",
        label: "Subscribers",
        icon: <NavIcon><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" /></NavIcon>,
      },
    ],
  },
];

export default function CreatorDashboardLayout({ children, activePath }) {
  const location = useLocation();
  const { userProfile, currentUser } = useAuth();
  const [studioData, setStudioData] = useState(null);
  const [adminBadges, setAdminBadges] = useState({
    pendingCreators: 0,
    waitlistedFans: 0,
    openTickets: 0,
    highPriorityTickets: 0,
  });

  const nicheMeta = getNicheMeta(userProfile?.niche);
  const nicheTheme = getNicheThemeStyle(nicheMeta.key);
  const handle = userProfile?.handle || userProfile?.username || "creator";
  const displayName = userProfile?.displayName || handle;
  const avatar = userProfile?.avatar || userProfile?.avatarUrl || "https://i.pravatar.cc/80";
  const founding = isFoundingCreator(userProfile);
  const showAdminTools = isSuperAdmin(userProfile);

  useEffect(() => {
    if (!currentUser?.uid) return;
    loadCreatorStudioData(currentUser.uid).then(setStudioData).catch(() => setStudioData(null));
  }, [currentUser?.uid]);

  // Badge counts — only when superadmin. Shares app-wide poller (no onSnapshot).
  useEffect(() => {
    if (!showAdminTools) return undefined;
    const unsubscribe = subscribeAdminBadges(setAdminBadges);
    return () => {
      unsubscribe();
    };
  }, [showAdminTools]);

  const sidebarStats = useMemo(() => {
    const drops = studioData?.drops || [];
    const scheduledCount = drops.filter((d) => {
      if (!d.date) return false;
      const release = new Date(d.date);
      return !Number.isNaN(release.getTime()) && release > new Date();
    }).length;

    return {
      subs: studioData?.stats?.subscriberCount ?? userProfile?.supporterCount ?? userProfile?.followerCount ?? 0,
      drops: studioData?.stats?.totalDrops ?? drops.length,
      ig: userProfile?.social?.instagramFollowers ?? 0,
      openCount: studioData?.stats?.openCount ?? 0,
      scheduledCount,
    };
  }, [studioData, userProfile]);

  const monthEarnings = studioData?.drops
    ? computeMonthlyEarnings(studioData.drops, userProfile).net
    : Number(studioData?.earnings?.monthToDate || 0);

  const payoutSub = monthEarnings > 0
    ? `${formatMoney(monthEarnings)} payout ${nextPayoutDate()}`
    : "No payout scheduled yet";

  const current = activePath || location.pathname;

  const resolveTo = (link) => {
    if (link.dynamicTo === "edit") return `/creator/${handle}/edit`;
    return link.to;
  };

  const isActive = (link) => {
    const to = resolveTo(link);
    if (to === "/dashboard") return current === "/dashboard";
    if (link.dynamicTo === "edit") {
      return current === to || current.startsWith(`${to}/`);
    }
    return current === to || current.startsWith(`${to}/`);
  };

  const earningsActive = current === "/earnings" || current.startsWith("/earnings/");

  return (
    <div className="creator-studio" style={nicheTheme}>
      <div className="cs-layout">
        <aside className="cs-sidebar">
          <div className="cs-sb-av-wrap">
            <img className="cs-sb-av" src={avatar} alt="" />
            <div className="cs-sb-name">{displayName}</div>
            <div className="cs-sb-handle">@{handle}</div>
            {founding && (
              <div className="cs-sb-founding">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="var(--gold)" stroke="none" aria-hidden>
                  <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z" />
                </svg>
                Founding Creator
              </div>
            )}
            <div className="cs-sb-stat-row">
              <div className="cs-sb-stat">
                <div className="cs-sb-stat-num">{formatCount(sidebarStats.subs)}</div>
                <div className="cs-sb-stat-lbl">Subs</div>
              </div>
              <div className="cs-sb-stat">
                <div className="cs-sb-stat-num">{sidebarStats.drops}</div>
                <div className="cs-sb-stat-lbl">Drops</div>
              </div>
              <div className="cs-sb-stat">
                <div className="cs-sb-stat-num">{formatCount(sidebarStats.ig)}</div>
                <div className="cs-sb-stat-lbl">IG</div>
              </div>
            </div>
          </div>

          {/* Superadmin only — not in DOM for other creators */}
          {showAdminTools && (
            <>
              <div className="cs-sidebar-label">Admin</div>
              {ADMIN_TOOL_LINKS.map((link) => {
                const count = link.badgeKey ? adminBadges[link.badgeKey] : 0;
                const isHigh = link.highKey && adminBadges[link.highKey] > 0;
                const badgeClass = isHigh ? "cs-badge-red" : link.badgeClass || "";
                const active = current === link.to || current.startsWith(`${link.to}/`);
                return (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`cs-sidebar-item${active ? " active" : ""}`}
                  >
                    {link.icon}
                    {link.label}
                    {count > 0 && (
                      <span className={`cs-sidebar-badge ${badgeClass}`}>{count}</span>
                    )}
                  </Link>
                );
              })}
              <div className="cs-sb-divider" />
            </>
          )}

          {NAV_SECTIONS.map((section) => (
            <div key={section.label}>
              <div className="cs-sidebar-label">{section.label}</div>
              {section.links.map((link) => {
                const to = resolveTo(link);
                const badgeValue = link.badgeKey ? sidebarStats[link.badgeKey] : 0;
                const badgeText = link.staticBadge || (badgeValue > 0 && link.badgeFormat ? link.badgeFormat(badgeValue) : null);

                return (
                  <Link
                    key={link.label}
                    to={to}
                    className={`cs-sidebar-item${isActive(link) ? " active" : ""}`}
                  >
                    {link.icon}
                    {link.label}
                    {badgeText && (
                      <span className={`cs-sidebar-badge ${link.badgeClass || ""}`}>
                        {badgeText}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          ))}

          <div className="cs-sb-divider" />

          <Link to="/earnings" className={`cs-sb-earnings${earningsActive ? " active" : ""}`}>
            <div className="cs-se-label">This month</div>
            <div className="cs-se-amount">{formatMoneyWhole(monthEarnings)}</div>
            <div className="cs-se-sub">{payoutSub}</div>
            <span className="cs-se-hand">after platform fee</span>
          </Link>
        </aside>

        <main className="cs-main">{children}</main>
      </div>
    </div>
  );
}
